import SubDefaultLayout from "../../components/SubDefaultLayout";
import { Card, Col, Row, Typography } from "antd";
import { StyleSheet } from "../../StyleSheet";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../stores/slices/generalDataSlice";
import { useCallback, useEffect, useState } from "react";
import { AppDispatch } from "../../stores/store";
import _ from "lodash";
import Loading from "../../components/Loading";
import InfoCardComponent from "../../components/InfoCardComponent";
import { getBillingData, getBillingPDF, getInvoiceData } from "../../methods/Methods";
import { ResponseGetListaFacturasFactura } from "../../types/common";
import { DownloadOutlined } from "@ant-design/icons";
import { AlertSliceInterface, clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import { saveFile } from "../../assets/mexicoStates/SaveFile";
const { Text } = Typography;

const DownloadBilling = () => {
    const dispatch = useDispatch<AppDispatch>();
    const generalData = useSelector(getGeneralData);
    const [idCliente, setIdCliente] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [billings, setBillings] = useState<ResponseGetListaFacturasFactura[]>([]);
    const [hasBilling, setHasBilling] = useState(false);
    const [message, setMessage] = useState("");

    const formatDate = (dateString: string) => {
        const [year, month, day] = dateString.split(" ")[0].split("-");
        return `${day}-${month}-${year}`;
    };

    const handleDispatchAlertModal = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
    }, [dispatch]);

    const openAlertModal = useCallback(
        (option: AlertSliceInterface) => {
            dispatch(
                setAlert({
                    show: true,
                    type: option.type as "INFO" | "SUCCESS" | "ERROR",
                    title: option.title,
                    message: option.message,
                    onFinalize: option.onFinalize,
                })
            );
        }, [dispatch]
    );

    useEffect(() => {
        if (hasBilling && idCliente) {
            // Facturas
            getBillingData(idCliente)
                .then((response) => {
                    if (response.data && response.data.data[0].facturas && response.data.data[0].facturas.length > 0) {
                        setBillings(response.data.data[0].facturas);
                    } else {
                        setMessage("No hay facturas para mostrar.");
                    }
                })
                .catch(() => {
                    setMessage("Hubo un error al procesar su solicitud. \nIntente más tarde.");
                });
        }
    }, [hasBilling, idCliente]);

    const fetchData = useCallback(async () => {
        setHasBilling(false);
        setIsLoading(true);

        // Obtener los datos de facturacion
        await getInvoiceData(idCliente)
            .then((response) => {
                if (response && response.data && response.data.data[0] && !_.has(response.data.data[0], 'estatus')) {
                    setHasBilling(true);
                } else {
                    setMessage("No cuenta con datos de facturación.");
                }
            })
            .catch(() => {
                setMessage("Hubo un error al procesar su solicitud. \nIntente más tarde.");
            });

        setIsLoading(false);
        setTimeout(() => {
            setIdCliente(idCliente);
            setIsLoading(false);
        }, 500);
    }, [idCliente]);

    useEffect(() => {
        if (!_.isEmpty(idCliente)) {
            fetchData();
        }
    }, [idCliente, fetchData]);


    useEffect(() => {
        if (generalData.selectedContract !== null) {
            setIdCliente(
                generalData.selectedContract.sucursal +
                generalData.selectedContract.suscriptor
            );
        }
    }, [generalData.selectedContract]);

    const getBillingPDFFunction = async (element: ResponseGetListaFacturasFactura) => {
        let billingPDF: string = "";
        const searchBillingMatched = billings.find((billing) => {
            const dateElement = new Date(element.fecha);
            const dateBilling = new Date(billing.fecha);
            const folioElement = element.folio;
            const folioBilling = billing.folio;

            // Comparar las fechas
            return dateElement.getTime() === dateBilling.getTime() && folioElement === folioBilling;
        });

        if (searchBillingMatched) {
            await getBillingPDF(
                searchBillingMatched.emisor,
                searchBillingMatched.serie,
                searchBillingMatched.folio
            )
                .then((response) => {
                    // Se pregunta si hay una respuesta y la respuesta no está vacía.
                    if (response.data && response.data.data[0]) {
                        // Se setean los datos necesarios
                        billingPDF = response.data.data[0].archivo;
                    }
                })
                .catch(() => {
                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: "ERROR",
                        title: "¡Estimado Usuario!",
                        message: "No fue posible obtener la factura. Intentelo más tarde.",
                        onFinalize: () => handleDispatchAlertModal(),
                    };
                    openAlertModal(dispatchAlert);
                });
            // Se pregunta si el PDF contiene algo.
            if (billingPDF !== "" && !_.isEmpty(searchBillingMatched.folio)) {
                downloadAccountInvoicePDF(billingPDF, searchBillingMatched.folio);
            } else {
                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: "ERROR",
                    title: "¡Estimado Usuario!",
                    message: "No fue posible obtener la factura. Intentelo más tarde.",
                    onFinalize: () => handleDispatchAlertModal(),
                };
                openAlertModal(dispatchAlert);
            }
        }
    };

    const downloadAccountInvoicePDF = async (pdf: string, folio: string) => {
        if (pdf && folio) {
            saveFile(pdf, "application/pdf", `factura_${folio}.pdf`);
            let dispatchAlert: AlertSliceInterface = {
                show: true,
                type: "SUCCESS",
                title: "¡Estimado Usuario!",
                message: "¡Su factura se ha descargado con éxito!",
                onFinalize: () => handleDispatchAlertModal(),
            };
            openAlertModal(dispatchAlert);
        }
    };

    return (
        <>
            {
                isLoading ?
                    <Loading />
                    :
                    <SubDefaultLayout title={"Descargar Facturas"} needReturn={true} navigateTo="/billing">
                        {
                            hasBilling && !_.isEmpty(billings) ? (
                                <Row style={styles.mainContainer} justify={"center"}>
                                    <Col span={12}>
                                        {billings.map((item, index) => (
                                            <Card key={index} style={styles.cardStyles}>
                                                <Row align={"middle"} justify={"space-between"}>
                                                    <Col xs={20} lg={22}>
                                                        <Text style={styles.textView}>{"Fecha: "}</Text>&nbsp;
                                                        <Text style={{ ...styles.textView, fontWeight: 700 }}>{formatDate(item.fecha)}</Text>
                                                    </Col>
                                                    <Col xs={4} lg={2}
                                                        style={{ cursor: "pointer", }}
                                                        onClick={() => getBillingPDFFunction(item)}>
                                                        <DownloadOutlined
                                                            style={styles.buttonStyle}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Card>
                                        ))}
                                    </Col>
                                </Row>
                            ) : (
                                <Row justify={"center"}>
                                    <Col span={12}>
                                        <InfoCardComponent
                                            color="#FFC439"
                                            colorText="#000"
                                            title="¡Estimado usuario!"
                                            description={message}
                                        />
                                    </Col>
                                </Row>
                            )
                        }
                    </SubDefaultLayout>
            }
        </>
    );
};

const styles = StyleSheet.create({
    textView: {
        color: "#757982",
        fontWeight: 600,
        fontSize: "1.25em"
    },
    mainContainer: {
        marginTop: 20,
    },
    cardStyles: {
        marginBottom: 15,
        boxShadow: "0px 0px 8px #00000029",
        borderRadius: 15,
    },
    buttonStyle: {
        border: "none",
        color: "#0055c2",
        backgroundColor: 'transparent',
        fontSize: 22
    },
});

export default DownloadBilling;
